import {configureStore} from "@reduxjs/toolkit";
import {createLogger} from "redux-logger";
import {loadState, saveState} from "helpers/localStorage";
import {PersistedState, RootState} from "types/store.types";
import {SET_TRACKED_EVENT_DATA} from "./eventTrackingActionTypes";
import {marketMapInitialState} from "./market-map/marketMapReducers";
import rootReducer from "./Reducers";
import {TUserReducerState} from "store/user/types";
import {TTargetListReducerState} from "store/target-list/types";
import {TSearchReducerStates} from "store/search/types";
import {TProjectListReducerState} from "store/project-list/types";
import {MapxProjectReducerState} from "store/project/types";
import {TMarketReportReducerState} from "store/market-report/types";
import {TUserPreferenceInitialState} from "store/userPreference/types";
import {TFilterReducerState} from "store/filter/types";
import {TUserCursorEventReducerState} from "store/events/types";
import {TCompanyReducerState} from "store/company/companyReducer.types";
import {TCandidateReducerState} from "store/candidate/candidateReducer.types";
import {TBackgroundTaskReducerInitialState} from "store/background-tasks/types";
import {TAdditionalProfilesReducerState} from "store/additional-profiles/types";
import {reportBuilderInitialState} from "store/report-builder/reportBuilderSlice";

const persistedState = loadState();

export const store = configureStore({
	reducer: rootReducer,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preloadedState: persistedState as any,
	middleware: (getDefaultMiddleware) => {
		const base = getDefaultMiddleware();

		if (process.env.REACT_APP_REDUX_LOGGER === "true") {
			const logger = createLogger({
				predicate: (_, action) => action.type !== SET_TRACKED_EVENT_DATA,
				collapsed: true,
				duration: true,
				diff: true,
			});

			return base.concat(logger);
		}

		return base;
	},
	devTools: process.env.NODE_ENV !== "production",
});

store.subscribe(() => {
	const state = store.getState();

	const stateToPersist: Partial<PersistedState> = {
		user: {
			...((state?.user as TUserReducerState) || {}),
			isTutorialModalOpened: false,
			updatingUsers: [],
		},
		organisation: state.organisation,
		search: {
			...(state.search as TSearchReducerStates),
			searchedData: {
				results: [],
				companiesPagination: null,
				candidatesPagination: null,
			},
			getSearchDataInProgress: null,
			searchDataError: null,
		},
		targetList: {
			...(state.targetList as TTargetListReducerState),
			targetListCandidateInProgress: false,
			targetListCompanyInProgress: false,
		},
		projectList: {
			...(state.projectList as TProjectListReducerState),
			projects: [],
		},
		candidate: {...(state.candidate as TCandidateReducerState)},
		bulkImport: {...state.bulkImport},
		backgroundTask: {...(state.backgroundTask as TBackgroundTaskReducerInitialState)},
		refresh_candidate: state.refresh_candidate,
		company: {
			...(state.company as TCompanyReducerState),
			currentCompany: null,
		},
		filter: {...(state.filter as TFilterReducerState)},
		mapxProject: {
			...(state.mapxProject as MapxProjectReducerState),
			onboardingStep: 0,
			openOnboardingComponent: false,
		},
		events: {...(state.events as TUserCursorEventReducerState)},
		additional_profiles: {
			...(state.additional_profiles as TAdditionalProfilesReducerState),
			ap_filters: (state.additional_profiles as TAdditionalProfilesReducerState).ap_filters,
		},
		market_map: {
			...marketMapInitialState,
			created_by: state.market_map.created_by,
		},
		marketReport: {
			...(state.marketReport as TMarketReportReducerState),
			results: [],
		},
		layout: state.layout,
		userPreference: {...(state.userPreference as TUserPreferenceInitialState)},
		// Only the drafts outlive a reload; a running export cannot resume after one
		reportBuilder: {...reportBuilderInitialState, drafts: state.reportBuilder.drafts},
	};

	saveState(stateToPersist);
});

if (process.env.NODE_ENV === "development") {
	console.info("🏪 Redux store initialized");
	console.info("📦 Initial state:", store.getState());
}

export type {RootState};

export default store;
